/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

const createItem = (orderNumber = "") => ({
  orderNumber,
  itemName: "",
  unit: "",
  code: "",
  location: "",
  bookQuantity: "",
  bookCost: "",
  bookRemainingValue: "",
  inventoryQuantity: "",
  inventoryCost: "",
  inventoryRemainingValue: "",
  diffQuantity: "",
  diffCost: "",
  diffRemainingValue: "",
  note: "",
});

const defaultItems = [createItem("1"), createItem("2"), createItem("3")];

function InventorySummaryTable({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  const [headerInfo, setHeaderInfo] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    invHour: "",
    invDay: today.getDate().toString(),
    invMonth: (today.getMonth() + 1).toString(),
    invYear: today.getFullYear().toString(),
    committee: [
      { name: "", position: "", representative: "", role: "Chủ tịch Hội đồng" },
      { name: "", position: "", representative: "", role: "Ủy viên" },
      { name: "", position: "", representative: "", role: "Ủy viên" },
    ],
    signingDate: today.toISOString().split("T")[0],
    directorName: "",
    accountantName: "",
    headOfCommitteeName: "",
  });

  const [localItems, setLocalItems] = useState(defaultItems);

  // Sync local state with formik values when loaded
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(values.items);
    }
  }, [values.items]);

  useEffect(() => {
    setHeaderInfo((prev) => ({
      ...prev,
      unit: values.unit || prev.unit,
      department: values.department || prev.department,
      documentNumber: values.documentNumber || prev.documentNumber,
      invHour: values.invHour || prev.invHour,
      invDay: values.invDay || prev.invDay,
      invMonth: values.invMonth || prev.invMonth,
      invYear: values.invYear || prev.invYear,
      committee: values.committee || prev.committee,
      signingDate: values.signingDate || prev.signingDate,
      directorName: values.directorName || prev.directorName,
      accountantName: values.accountantName || prev.accountantName,
      headOfCommitteeName: values.headOfCommitteeName || prev.headOfCommitteeName,
    }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) {
      onChangeValue(field, val);
    }
  };

  const handleCommitteeChange = (index, field, val) => {
    const newCommittee = [...headerInfo.committee];
    newCommittee[index] = { ...newCommittee[index], [field]: val };
    setHeaderInfo((prev) => ({ ...prev, committee: newCommittee }));
    if (onChangeValue) {
      onChangeValue("committee", newCommittee);
    }
  };

  const handleChangeItem = (index, field, value) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Auto calculate diff
    if (["bookQuantity", "inventoryQuantity"].includes(field)) {
      const bq = parseFloat(newItems[index].bookQuantity) || 0;
      const iq = parseFloat(newItems[index].inventoryQuantity) || 0;
      newItems[index].diffQuantity = iq - bq;
    }
    if (["bookCost", "inventoryCost"].includes(field)) {
      const bc = parseFloat(newItems[index].bookCost) || 0;
      const ic = parseFloat(newItems[index].inventoryCost) || 0;
      newItems[index].diffCost = ic - bc;
    }
    if (["bookRemainingValue", "inventoryRemainingValue"].includes(field)) {
      const brv = parseFloat(newItems[index].bookRemainingValue) || 0;
      const irv = parseFloat(newItems[index].inventoryRemainingValue) || 0;
      newItems[index].diffRemainingValue = irv - brv;
    }

    setLocalItems(newItems);
  };

  const addRow = () => {
    const newItem = createItem((localItems.length + 1).toString());
    setLocalItems([...localItems, newItem]);
  };

  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    const updated = localItems.filter((_, i) => i !== index);
    setLocalItems(updated);
  };

  const calculatedTotal = useMemo(() => {
    const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

    const fieldsToSum = [
      "bookCost",
      "bookRemainingValue",
      "inventoryCost",
      "inventoryRemainingValue",
      "diffCost",
      "diffRemainingValue",
    ];

    const total = {};
    fieldsToSum.forEach((field) => {
      total[field] = localItems.reduce((acc, item) => acc + num(item[field]), 0);
    });

    return total;
  }, [localItems]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("total", calculatedTotal);
    }
  }, [localItems, calculatedTotal]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "40px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "20px 0" },
    titleMain: { fontSize: "18px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    totalRowBg: { backgroundColor: "#fff", fontWeight: "bold", textAlign: "center" },
    flexRow: { display: "flex", alignItems: "center", marginBottom: "8px" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "40%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.unit}
              onChange={(e) => handleHeaderChange("unit", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Bộ phận:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.department}
              onChange={(e) => handleHeaderChange("department", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số 05 - TSCĐ</div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>BIÊN BẢN TỔNG HỢP KIỂM KÊ TÀI SẢN CỐ ĐỊNH</h2>
        <div style={{ textAlign: "right", marginTop: 10 }}>
          Số:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "100px" }}
            value={headerInfo.documentNumber}
            onChange={(e) => handleHeaderChange("documentNumber", e.target.value)}
          />
        </div>
      </div>

      {/* THÔNG TIN KIỂM KÊ */}
      <div style={{ marginBottom: "10px" }}>
        <div style={{ marginBottom: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Thời điểm kiểm kê: </label>
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 8 }}
            value={headerInfo.invHour}
            onChange={(e) => handleHeaderChange("invHour", e.target.value)}
            placeholder="giờ"
          />{" "}
          giờ,
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 8 }}
            value={headerInfo.invDay}
            onChange={(e) => handleHeaderChange("invDay", e.target.value)}
            placeholder="ngày"
          />{" "}
          ngày
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 8 }}
            value={headerInfo.invMonth}
            onChange={(e) => handleHeaderChange("invMonth", e.target.value)}
            placeholder="tháng"
          />{" "}
          tháng
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "60px", marginLeft: 8 }}
            value={headerInfo.invYear}
            onChange={(e) => handleHeaderChange("invYear", e.target.value)}
            placeholder="năm"
          />{" "}
          năm.
        </div>
        <div style={{ marginBottom: 5 }}>Ban kiểm kê gồm:</div>
        {headerInfo.committee.map((member, index) => (
          <div key={index} style={s.flexRow}>
            - Ông/Bà:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", margin: "0 10px" }}
              value={member.name}
              onChange={(e) => handleCommitteeChange(index, "name", e.target.value)}
            />
            Chức vụ{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "150px", margin: "0 10px" }}
              value={member.position}
              onChange={(e) => handleCommitteeChange(index, "position", e.target.value)}
            />
            Đại diện{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "150px", margin: "0 10px" }}
              value={member.representative}
              onChange={(e) => handleCommitteeChange(index, "representative", e.target.value)}
            />
            {member.role}
          </div>
        ))}
        <div style={{ marginTop: "10px" }}>Đã kiểm kê TSCĐ, kết quả như sau:</div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...s.th, width: "40px" }}>
                Số
                <br />
                TT
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "160px" }}>
                Tên
                <br />
                TSCĐ
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "50px" }}>
                Đơn
                <br />
                vị
                <br />
                tính
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Mã
                <br />
                số
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Nơi
                <br />
                sử
                <br />
                dụng
              </th>
              <th colSpan={3} style={s.th}>
                Theo sổ kế toán
              </th>
              <th colSpan={3} style={s.th}>
                Theo kiểm kê
              </th>
              <th colSpan={3} style={s.th}>
                Chênh lệch
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Ghi chú
              </th>
              {!containView && (
                <th rowSpan={2} style={{ ...s.th, width: "30px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "60px" }}>
                Số
                <br />
                lượng
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                còn
                <br />
                lại
              </th>
              <th style={{ ...s.th, width: "60px" }}>
                Số
                <br />
                lượng
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                còn lại
              </th>
              <th style={{ ...s.th, width: "60px" }}>
                Số
                <br />
                lượng
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                còn lại
              </th>
            </tr>
            <tr style={{ backgroundColor: "#fafafa" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
              <th style={s.th}>F</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.orderNumber || ""}
                    onChange={(e) => handleChangeItem(i, "orderNumber", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.itemName || ""}
                    onChange={(e) => handleChangeItem(i, "itemName", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.unit || ""}
                    onChange={(e) => handleChangeItem(i, "unit", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.code || ""}
                    onChange={(e) => handleChangeItem(i, "code", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.location || ""}
                    onChange={(e) => handleChangeItem(i, "location", e.target.value)}
                  />
                </td>

                {/* Data columns */}
                {[
                  "bookQuantity",
                  "bookCost",
                  "bookRemainingValue",
                  "inventoryQuantity",
                  "inventoryCost",
                  "inventoryRemainingValue",
                  "diffQuantity",
                  "diffCost",
                  "diffRemainingValue",
                ].map((field) => (
                  <td key={field} style={s.td}>
                    <input
                      disabled={containView}
                      type="number"
                      style={s.inputTable}
                      value={row[field] || ""}
                      onChange={(e) => handleChangeItem(i, field, e.target.value)}
                    />
                  </td>
                ))}

                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.note || ""}
                    onChange={(e) => handleChangeItem(i, "note", e.target.value)}
                  />
                </td>
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* Total Row */}
            <tr style={s.totalRowBg}>
              <td colSpan={5} style={s.td}>
                Cộng
              </td>
              <td style={s.td}>x</td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.bookCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.bookRemainingValue)}</span>
              </td>
              <td style={s.td}>x</td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.inventoryCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.inventoryRemainingValue)}</span>
              </td>
              <td style={s.td}>x</td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.diffCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.diffRemainingValue)}</span>
              </td>
              <td style={s.td}>x</td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={4} display="flex" justifyContent="flex-end">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              THÊM DÒNG
            </SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "33%" }}>
          <b>Giám đốc</b>
          <div style={{ fontStyle: "italic", fontSize: "13px" }}>
            (Ghi ý kiến giải quyết số chênh lệch)
          </div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "50px" }}>
            (Ký, họ tên, đóng dấu)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.directorName}
            onChange={(e) => handleHeaderChange("directorName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <b>Kế toán trưởng</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.accountantName}
            onChange={(e) => handleHeaderChange("accountantName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày {headerInfo.signingDate ? new Date(headerInfo.signingDate).getDate() : "..."} tháng{" "}
            {headerInfo.signingDate ? new Date(headerInfo.signingDate).getMonth() + 1 : "..."} năm{" "}
            {headerInfo.signingDate ? new Date(headerInfo.signingDate).getFullYear() : "..."}
          </div>
          <b>Trưởng Ban kiểm kê</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "50px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.headOfCommitteeName}
            onChange={(e) => handleHeaderChange("headOfCommitteeName", e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: "40px", fontSize: "14px" }}>
        <b>Ghi chú:</b> <i style={{ textDecoration: "underline" }}>Tùy</i>{" "}
        <i style={{ fontStyle: "italic" }}>
          theo đặc điểm hoạt động sản xuất kinh doanh và yêu cầu quản lý của đơn vị mình, doanh
          nghiệp được xây dựng, thiết kế biểu mẫu chứng từ kế toán.
        </i>
      </div>
    </SoftBox>
  );
}

InventorySummaryTable.propTypes = {
  formData: PropTypes.object,
};

export default InventorySummaryTable;
